import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { formatPostDate, usePostBySlug } from '../api/blog';
import { articleMeta, usePageMeta } from '../seo/seo';

const BlogDetailPage = () => {
  const location = useLocation();
  const { slug } = useParams();
  const { post, status, reload } = usePostBySlug(slug, location.state?.post);
  usePageMeta(post ? articleMeta(post) : undefined);

  if (!post) {
    if (status === 'loading') return <DetailLoading />;
    return <DetailNotFound kind="article" onRetry={status === 'error' ? reload : undefined} />;
  }

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="bg-gradient-to-r from-brandPurple to-brandBlue">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> All posts
          </Link>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {formatPostDate(post) && (
          <p className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Calendar className="w-4 h-4" /> {formatPostDate(post)}
          </p>
        )}
        <h1 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight">{post.title}</h1>
        {post.content.image_url && (
          <img
            src={post.content.image_url}
            alt=""
            className="mt-8 w-full aspect-video object-cover rounded-2xl shadow-lg bg-gray-100 dark:bg-gray-800"
          />
        )}
        <div className="mt-10 space-y-5 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
          {(post.content.blog_text ?? '').split(/\n\s*\n/).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </article>
    </div>
  );
};

export const DetailLoading = () => (
  <div className="max-w-5xl mx-auto px-6 md:px-8 py-14 animate-pulse" aria-label="Loading">
    <div className="grid gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded-2xl" />
      <div className="space-y-4">
        <div className="h-5 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-9 w-full bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-9 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
  </div>
);

export const DetailNotFound = ({ kind, onRetry }: { kind: 'episode' | 'article'; onRetry?: () => void }) => (
  <div className="max-w-3xl mx-auto px-6 py-24 text-center">
    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
      {onRetry ? `We couldn’t load this ${kind}` : `This ${kind} isn’t available`}
    </h1>
    <p className="text-gray-600 dark:text-gray-300 mb-8">
      {onRetry ? 'Please check your connection and try again.' : 'It may have been moved or removed.'}
    </p>
    <div className="flex flex-wrap justify-center gap-4">
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="px-6 py-3 bg-[#3399ff] text-white rounded-lg hover:bg-opacity-90 transition-all duration-300"
        >
          Try again
        </button>
      )}
      <Link
        to="/blogs"
        className="px-6 py-3 border border-[#3399ff] text-[#3399ff] rounded-lg hover:bg-[#3399FF] hover:text-white transition-colors"
      >
        Back to Blog & Podcast
      </Link>
    </div>
  </div>
);

export default BlogDetailPage;
